// Entra app roles. Every admin API route checks these on the server from the validated token's
// `roles` claim; hiding a page in the web app is never the control.

// Each includes the ones below it: HR.Admin > HR.Manager > HR.Agent.
//   HR.Agent:   the Talk to HR queue and analytics.
//   HR.Manager: that, plus documents: review, Applies to, location variants, sync and publish.
//   HR.Admin:   that, plus sources (which SharePoint sites, folders and websites Ask HR reads).
export const Role = {
  Admin: "HR.Admin",
  Manager: "HR.Manager",
  Agent: "HR.Agent",
} as const;

export type RoleName = (typeof Role)[keyof typeof Role];

const IMPLIES: Record<RoleName, RoleName[]> = {
  [Role.Admin]: [Role.Admin, Role.Manager, Role.Agent],
  [Role.Manager]: [Role.Manager, Role.Agent],
  [Role.Agent]: [Role.Agent],
};
const KNOWN = new Set<unknown>(Object.values(Role));

// API Gateway's JWT authorizer hands array claims to Lambda flattened into one bracketed string,
// "[HR.Admin HR.Agent]"; MSAL in the browser sees a real array. Accept both, nothing else.
function rawRoles(roles: unknown): unknown[] {
  if (Array.isArray(roles)) return roles;
  const m = typeof roles === "string" ? /^\[(.*)\]$/.exec(roles) : null;
  return m?.[1] ? m[1].split(/[\s,]+/).filter(Boolean) : [];
}

export function rolesFromClaims(claims: { roles?: unknown }): RoleName[] {
  const raw = rawRoles(claims.roles);
  const known = raw.filter((r): r is RoleName => KNOWN.has(r));
  return [...new Set(known.flatMap((r) => IMPLIES[r]))];
}

// Employee: everyone in "Ask HR - Users". Not checked by any route (being signed in is the gate,
// since the app is assignment required), but every assignment grants it or an HR role, so a
// signed-in account with none of them isn't on the access list (the web app's No access page).
export const EMPLOYEE = "Employee";

export function hasAnyRole(claims: { roles?: unknown }): boolean {
  return rawRoles(claims.roles).some((r) => r === EMPLOYEE || KNOWN.has(r));
}

export function hasRole(claims: { roles?: unknown }, role: RoleName): boolean {
  return rolesFromClaims(claims).includes(role);
}

export class ForbiddenError extends Error {
  readonly status = 403;
  constructor(role: RoleName) {
    super(`requires ${role}`);
    this.name = "ForbiddenError";
  }
}

export function requireRole(claims: { roles?: unknown }, role: RoleName): void {
  if (!hasRole(claims, role)) throw new ForbiddenError(role);
}
